import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import useDocumentTitle from '@hooks/useDocumentTitle'
import { useInvalidateSchedules, useSchedulesQuery } from '@hooks/useSchedules'
import { useResultMaxAgeDays } from '@hooks/useResultMaxAgeDays'
import Modal from '@components/common/Modal'
import ToggleSwitch from '@components/common/ToggleSwitch'
import RecurrenceEditor from '@components/reports/queue/RecurrenceEditor'
import { rollsForward, reportNumberOf } from '@components/reports/queue/scheduleCapabilities'
import { firmTextClass, isExagoReportId, unnamedReportLabel } from '@components/reports/queue/jobIdentity'
import FirmText from '@components/reports/queue/FirmText'
import SchedulesTable from '@components/reports/queue/SchedulesTable'
import ScheduleRunHistory from '@components/reports/queue/ScheduleRunHistory'
import ScheduleCalendarPanel from '@components/reports/queue/ScheduleCalendarPanel'
import QueueReportModal from '@components/reports/queue/QueueReportModal'
import QueueCatalogUnreadable from '@components/reports/queue/QueueCatalogUnreadable'
import EmailRecipientsField from '@components/reports/queue/EmailRecipientsField'
import {
  recipientsFormatSwitch, keptRecipientsNote, ownerNeedsAddress, ownerAddressError, normalizeOwnerAddress,
} from '@components/reports/queue/emailRecipients'
import useOwnDeliveryAddress from '@components/reports/queue/useOwnDeliveryAddress'
import { useQueueReportPicker } from '@components/reports/queue/useQueueReportPicker'
import reportScheduleService from '@api/v1/reportScheduleService'
import reportService from '@api/v1/reportService'
import reportViewsService from '@api/v1/reportViewsService'
import { toast } from '@store/toastStore'
import { QUEUE_PATH, REPORTS_LIST_PATH } from '@config/reportRoutes'
import { ChevronLeftIcon, InfoIcon } from '@components/reports/queue/queueIcons'
import { fmtWhenInZone } from '@components/reports/queue/queueFormat'
import { scheduleRetentionHelp, schedulesPageRetention } from '@components/reports/queue/resultRetention'
import { PRIVATE_SCHEDULE_HELP, PRIVATE_SCOPE_OPTION_LABEL } from '@components/reports/queue/jobVisibility'
import { SCHEDULES_PAGE_CAP } from '@config/queueConfig'

/**
 * Screen 6 — recurring schedules (specs 058 / 075).
 *
 * Built to the signed-off design HTML §6. The row layout, the action treatment and
 * the delete confirmation are all Himaad's review notes applied:
 *
 *   - one row per schedule in a table (see `SchedulesTable`);
 *   - ONE text action plus a pencil plus a kebab, not four competing buttons;
 *   - delete behind a confirmation, and last in the kebab behind a divider.
 *
 * Two rules this page refuses to break, both learned the hard way here:
 *
 *  - **No example rows.** If the list is empty it says so. A schedules table that
 *    looks populated but never fires is the worst possible outcome for a feature
 *    whose entire job is running reports while nobody is watching.
 *  - **Delete offers Pause first.** Deleting is how people try to stop a schedule
 *    for month-end close, and it throws away the tuned filter set.
 *
 * ## Creating a schedule (superseded rationale, 2026-08-27)
 *
 * This comment used to read: *"Creating a schedule happens FROM a report, because a
 * schedule needs that report's filters — so 'New schedule' leads to the catalog
 * rather than opening a form that could not know what to run. This page manages
 * what exists."*
 *
 * **That premise is false and is retained only so the old reasoning is not silently
 * rewritten.** A form that knows what to run already shipped: `QueueReportModal`
 * carries a report picker, reads the picked report's definition and seeds every
 * filter from that report's own defaults — which is exactly the objection above.
 * `ReportQueue.jsx` has rendered it that way since spec 057.
 *
 * What the stale premise actually protected was a BUG. `1d2e023` put an honest
 * `btn-cb-toolbar` **"Browse reports"** link here; `02604d9` relabelled it
 * **"New schedule"** and promoted it to `btn-cb-primary` while leaving
 * `to={REPORTS_LIST_PATH}` byte-identical, and `1a31a74` moved it into the
 * permanent primary-action slot. So the page's most prominent control promised
 * creation and delivered navigation, and no test pinned it — a relabel-only diff
 * shipped green. Jordan's call (2026-08-27): open the modal in place, sharing
 * `ReportQueue`'s machinery via `useQueueReportPicker` rather than building the
 * flow a third time.
 *
 * Creating from a report page is still the better path when you already have the
 * filters on screen, so the empty state points at it — as a sentence, not as a
 * second primary button aimed somewhere else.
 *
 * ## Visibility scope (CC-50282)
 *
 * The design's **Mine / My group / Everyone at the firm** selector is here, and it
 * is real: `?scope=` drives a server-scoped list, the group audience is resolved
 * from CB (never asserted by this client), and a failed lookup narrows to "mine"
 * and SAYS so instead of rendering as an empty group.
 *
 * Sharing is **view-only** by product decision. A row you do not own shows what it
 * is and whether it is healthy, and offers nothing to click — no toggle, no Run
 * now, no pencil, no kebab. Every mutating route is owner-scoped and would 404, so
 * offering the controls would only manufacture errors; and letting a colleague
 * pause someone's month-end pack is the silent failure this feature keeps trying
 * to eliminate.
 *
 * (This comment used to say the selector was deliberately absent because the model
 * could not express "my group". That was true until spec 078 gave `schedules` real
 * scope columns and a scoped list.)
 *
 * ## Spec 169 (CC-54214) — administrators and colleagues' PRIVATE schedules
 *
 * Jordan, 2026-10-13: "only admins should see and be able to edit schedules set to mine
 * in the firm wide queue." So, when cb-api says the reader is a CB administrator
 * (`adminView` on the list, `adminAccess` on each such row — CB's own answer, asked by
 * the worker on the request, never a token claim), a colleague's private schedule is
 * listed here too, marked "Private to {owner}", and offers Edit and Pause / Resume.
 * Editing goes through `AdminEditScheduleModal`, which can change only the name, the
 * timing, the date window and "Keep results": the schedule keeps running as its OWNER,
 * with their sign-in, their data access and their email delivery (cb-api refuses every
 * other field). Run now, Skip, Duplicate, Run history and Delete stay the owner's.
 * Everyone else's view of this page is unchanged.
 */

/** Where beta feedback goes. Unset ⇒ the calendar's feedback button is hidden. */
const FEEDBACK_EMAIL = import.meta.env.VITE_FEEDBACK_EMAIL || ''

/**
 * The scope selector's options.
 *
 * ## Why these are not the job list's words (QA, 2026-09-03)
 *
 * They used to be `Mine / My group / Everyone at the firm`, copied from the Queue
 * page deliberately — `scope.js` still says the contract is "identical to the job
 * list's so the two cannot drift". **It drifted at birth, and the labels hid it.**
 *
 * The two predicates are genuinely different:
 *
 *   schedules  `WHERE tenant=? AND (user_id=? OR scope='firm')`   ← opt-in publish
 *   jobs       `WHERE tenant=? AND mode=?`                        ← the whole tenant
 *
 * So "Everyone at the firm" meant "everyone's" on the Queue tab and "mine plus
 * what people shared" one tab over. Measured on QA: a colleague's schedule set to
 * `scope='mine'` is invisible here, while the RUN it produced is visible on the
 * Queue at the identically-labelled scope — the child of an unpublished parent.
 * Nothing on screen said so, which is what made it read as a bug. (⚠ Superseded by
 * spec 169: a private schedule's runs are no longer listed to non-administrator
 * colleagues on the Queue either, so the child now follows its parent.)
 *
 * The predicate is the intended model and is not changed: spec 078 §3.3 states it
 * in prose, its verification table pins it, `report-scope.test.js` asserts it with
 * a comment forbidding the wider reading, and saved views implement the same
 * opt-in publish — 2 of 3 surfaces already agree. On the merits it is also right:
 * a schedule is a standing instruction that runs unattended under its owner's
 * credential, and its name routinely carries a client or matter, so publishing
 * every partner's month-end pack by default is a disclosure nobody chose. Migration
 * 0008 backfilled every pre-existing row to `mine`, so widening would retroactively
 * publish names written when no sharing control existed at all.
 *
 * What changes is the WORDS, to the read-side vocabulary `ReportViewsMenu` already
 * uses for the same model. "Shared with…" describes an opt-in; "Everyone at…"
 * describes a roster.
 */
const SCOPES = ['mine', 'group', 'firm']
const SCOPE_LABEL = { mine: 'Mine', group: 'Shared with my group', firm: 'Shared with the firm' }

/** Spec 169 — who a schedule is visible to, in the delete confirmation's words. */
const deleteAudience = (s) => {
  const scope = s?.scope || (s?.shareable ? 'firm' : 'mine')
  if (scope === 'firm') return 'everyone at the firm'
  if (scope === 'group') return s?.groupName ? `the ${s.groupName} group` : 'your group'
  return 'only you and Centerbase administrators'
}

const ReportSchedules = () => {
  useDocumentTitle('Recurring schedules')
  const [searchParams, setSearchParams] = useSearchParams()
  const [editing, setEditing] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [history, setHistory] = useState(null)
  const [runningId, setRunningId] = useState(null)

  // Read the flag at render time (not a module-scope const): the route is already
  // gated elsewhere, and reading it here keeps the query testable under vi.stubEnv.
  const reportJobsOn = import.meta.env.VITE_REPORT_JOBS === 'true'

  // Visibility scope lives in the URL, per project convention — a scoped view is
  // shareable and survives back/forward.
  const scope = SCOPES.includes(searchParams.get('scope')) ? searchParams.get('scope') : 'mine'
  const setScope = useCallback((next) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      if (next && next !== 'mine') p.set('scope', next); else p.delete('scope')
      return p
    }, { replace: true })
  }, [setSearchParams])

  /*
   * Shared with the Queue page's count chip — one key, one freshness policy, and
   * (CC-53132) one read state.
   *
   * `state` / `ready` replace `isLoading` here. react-query defines `isLoading` as
   * `isPending && isFetching`, so it is FALSE for a query that is pending but not in
   * flight — above all a query react-query has PAUSED (default
   * `networkMode: 'online'` while `onlineManager` believes the browser is offline).
   * `queryFn` is never called, nothing throws, nothing is logged, `data` stays
   * undefined and `schedules` falls back to `[]`, so this page fell past its loading
   * branch and its error branch into "You don't have any schedules yet." about a
   * read that never happened.
   *
   * Of the pages carrying that defect this is the worst place for it, for the reason
   * in the header comment: the whole job of the feature is running reports while
   * nobody is watching. Someone who believes that sentence either re-creates a
   * schedule that already exists — two month-end packs, two deliveries — or
   * concludes their delivery was silently dropped.
   *
   * The state comes from the hook rather than a `useReadState` call here on purpose:
   * the hook already makes it (with the scope in its label) for the Queue chip's
   * benefit too, and a second call would log the same paused read twice.
   */
  const schedulesQuery = useSchedulesQuery({ scope })
  const { schedules, error, scopeResolved, refetch, state: schedulesState, ready: schedulesReady, adminView } = schedulesQuery
  const invalidate = useInvalidateSchedules()
  // Spec 152 review — the results bucket's age, for the page's retention panel. Read from
  // the same cached list this page just loaded (the envelope declares it).
  const resultMaxAgeDays = useResultMaxAgeDays()

  /**
   * Creation, in place (2026-08-27) — see the superseded rationale in the header.
   *
   * `initialWhen: 'repeat'` is the load-bearing argument and is passed explicitly.
   * `QueueReportModal` defaults it to `'now'` (QueueReportModal.jsx:67) and the
   * Queue page's host is happy with that, so copying that call site verbatim would
   * open a control labelled "New schedule" on **"Right now, in the background"** —
   * a one-off background job, no schedule created, nothing added to this list.
   * That is the same wrong-promise defect this change exists to fix, one layer
   * down. `ReportView.jsx:1533` and `SpecializedFilterBar.jsx:210` steer it for
   * the same reason.
   *
   * The hook calls `useInvalidateSchedules()` itself on a successful create, and
   * `SCHEDULES_QUERY_KEY` is the key PREFIX of this page's own
   * `['report-schedules', scope]` entry — so the new row appears here with no
   * manual refresh, at whatever scope is selected.
   */
  const picker = useQueueReportPicker({ initialWhen: 'repeat' })

  // Report names for the row's identity chip ("Aged A/R · #21"). The schedule row
  // carries only a reportId. Same cache key the Queue page uses, so on a normal
  // Queue → Schedules journey this is already warm.
  const { data: catalogTabs } = useQuery({
    queryKey: ['reports-list'],
    queryFn: () => reportService.list(''),
    staleTime: 5 * 60 * 1000,
    enabled: reportJobsOn,
  })
  const reportNames = useMemo(() => {
    const map = new Map()
    for (const t of catalogTabs || []) {
      for (const r of t.reports || []) if (r?.id != null) map.set(String(r.id), r.name)
    }
    return map
  }, [catalogTabs])

  const save = useMutation({
    mutationFn: ({ id, payload }) => reportScheduleService.update(id, payload),
    onSuccess: () => { invalidate(); setEditing(null) },
    onError: (e) => toast.error(e?.message || 'Could not save that schedule.'),
  })

  const remove = useMutation({
    mutationFn: (id) => reportScheduleService.remove(id),
    onSuccess: () => { invalidate(); setConfirmDelete(null); toast.success('Schedule deleted.') },
    onError: (e) => toast.error(e?.message || 'Could not delete that schedule.'),
  })

  const togglePaused = useCallback(
    (s) => save.mutate({ id: s.id, payload: { enabled: !s.enabled } }),
    [save]
  )

  /**
   * Run now. Reports both halves of the outcome — that it started, and where it
   * went — because a "Run now" whose result you then have to hunt for is the
   * complaint this button exists to answer.
   */
  const runNow = useCallback(async (s) => {
    setRunningId(s.id)
    try {
      await reportScheduleService.runNow(s.id)
      invalidate()
      // Spec 216: an Advanced Report schedule's name is the firm's by default — kept out of Pendo.
      const started = `${s.name || 'That report'} is running — it'll land in your queue.`
      toast.success(isExagoReportId(s.reportId) ? <FirmText reportId={s.reportId}>{started}</FirmText> : started)
    } catch (e) {
      toast.error(e?.message || 'Could not start that run.')
    } finally {
      setRunningId(null)
    }
  }, [invalidate])

  /**
   * Skip the next run. Names the run it dropped AND the one it moved to: "skipped"
   * with no dates is exactly the kind of half-confirmation that leaves someone
   * unsure whether month-end will fire.
   */
  const skipNext = useCallback(async (s) => {
    try {
      const res = await reportScheduleService.skipNext(s.id)
      invalidate()
      const dropped = res?.skippedRunAt ? fmtWhenInZone(res.skippedRunAt, s.timezone) : 'the next run'
      const next = res?.schedule?.nextRunAt ? fmtWhenInZone(res.schedule.nextRunAt, s.timezone) : 'its next slot'
      toast.success(`Skipped ${dropped}. Next run ${next}.`)
    } catch (e) {
      toast.error(e?.message || 'Could not skip that run.')
    }
  }, [invalidate])

  /**
   * Duplicate. The copy is made SERVER-side and arrives paused — see the note on
   * `reportScheduleService.duplicate`: a client-side copy would silently drop the
   * schedule's filters, and CB runs an unfiltered report rather than erroring.
   */
  const duplicate = useCallback(async (s) => {
    try {
      await reportScheduleService.duplicate(s.id)
      invalidate()
      toast.success('Copied — the duplicate is paused so you can adjust it first.')
    } catch (e) {
      toast.error(e?.message || 'Could not duplicate that schedule.')
    }
  }, [invalidate])

  const sendFeedback = useMemo(() => {
    if (!FEEDBACK_EMAIL) return undefined
    return () => {
      const subject = encodeURIComponent('Report Center — schedule calendar beta')
      window.location.href = `mailto:${FEEDBACK_EMAIL}?subject=${subject}`
    }
  }, [])

  const activeCount = schedules.filter((s) => s.enabled).length
  const ownedCount = schedules.filter((s) => s.isOwner !== false).length

  return (
    /*
     * A1 (2026-08-26) — a FRAGMENT, not a padded wrapper; see the identical note
     * in ReportQueue.jsx, whose root this was a byte-for-byte copy of. The
     * `p-4 sm:p-6 lg:p-8` here stacked on top of main's p-6 AND `.report-shell`,
     * so Queue and Schedules were the only two Report Center pages at 72px of
     * gutter (>=1024px) against everyone else's 40px, and being outside the
     * shell's max-width it shifted the centring too. `.report-shell` is now the
     * only width rule on this page.
     */
    <>
      {/* The Dashboards | Reports | Queue nav moved into the TOP BAR
          (ReportCenterNav, mounted by Header) — the Figma places it there, and
          three page-level copies of one nav had already drifted apart. */}

      <div className="report-shell">
        <div className="mb-4.5 flex flex-wrap items-start justify-between gap-3.5">
          <div>
            <div className="flex items-center gap-2">
              <Link to={QUEUE_PATH} aria-label="Back to the report queue" className="btn-cb-toolbar px-2 py-1.5">
                <ChevronLeftIcon />
              </Link>
              <h1 className="font-display text-2xl text-gray-900">Recurring schedules</h1>
            </div>
            {/* The design's count line. The second clause is the whole value
                proposition of the feature, so it is stated, not implied.

                The count is gated on a COMPLETED read, not on emptiness: "0 active"
                — or a stale "3 active" left over beside a failed refetch — is a
                claim about the user's schedules, and until the read lands we have
                no standing to make one. Unresolved falls back to the neutral line,
                which says what the page is for and counts nothing. */}
            <p className="mt-1.5 text-[11.5px] text-gray-500">
              {!schedulesReady || schedules.length === 0
                ? 'Reports that run on their own and land in your queue.'
                : scope === 'mine'
                  ? `${activeCount} active · these keep running whether or not you're signed in`
                  /* At a wider scope the count includes colleagues' schedules, so it
                     must not claim they are all yours. */
                  : `${activeCount} active visible to you · ${ownedCount} ${ownedCount === 1 ? 'is' : 'are'} yours`}
            </p>
          </div>
          {/* The design's scope selector (CC-50282). It renders now that the
              backend can answer it truthfully — see the note above about why it
              was deliberately absent until Phase 1 shipped. */}
          <div className="flex items-center gap-2">
            <label htmlFor="sched-scope" className="sr-only">Which schedules to show</label>
            {/* ADA (2026-08-26): every control border on this page moves from
                border-gray-200 (#e5e7eb, 1.24:1) to border-rc-control (#8b95a5,
                3.03:1). These are white-on-white selects and inputs, so the 1px
                boundary is the only thing that identifies them as controls and
                WCAG SC 1.4.11 wants 3.0:1 from it. Six of them here — this one
                plus the five in the edit modal below. Card outlines, dividers
                and the modal's own chrome stay light; they identify nothing. */}
            <select
              id="sched-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="rounded-lg border border-rc-control px-2.5 py-1.5 text-[12.5px]"
            >
              {SCOPES.map((s) => <option key={s} value={s}>{SCOPE_LABEL[s]}</option>)}
            </select>
            {/* States the sharing model out loud at the wider scopes, because the
                filter alone cannot: a short list is indistinguishable from
                "nobody shared anything", and that ambiguity is exactly what made
                QA read the opt-in model as a broken filter. */}
            {scope !== 'mine' && (
              <span className="text-[11.5px] text-gray-500" data-testid="schedules-sharing-note">
                {/* Spec 169: an administrator's list also carries colleagues' private
                    schedules, so the sentence for everyone else would be false for them. */}
                {adminView
                  ? 'Colleagues’ schedules appear here if they shared them. As a Centerbase administrator you also see their private ones.'
                  : <>Colleagues&apos; schedules appear here only if they shared them.</>}
              </span>
            )}
            {/* A BUTTON, not a Link. Until 2026-08-27 this was
                `<Link to={REPORTS_LIST_PATH}>` — the page's loudest control
                promised creation and delivered navigation to the catalog. */}
            <button type="button" onClick={picker.open} className="btn-cb-primary">New schedule</button>
          </div>
        </div>

        {/* The CB group lookup failed and the server narrowed to "mine". Saying so
            is the whole point: an empty group and a broken lookup look identical,
            and that ambiguity hid the CC-49595 bug through two QA rounds. */}
        {!scopeResolved && (
          <div
            role="status"
            /*
             * Needs its own handle as of spec 111, and the reason generalises.
             * This page now renders `DataGrid`, which uses dnd-kit for column
             * drag-reorder, and dnd-kit injects its OWN off-screen
             * `role="status"` live region (`DndLiveRegion-0`) to announce drags.
             * So `getByRole('status')` became ambiguous here the moment the grid
             * arrived — through no change to this element.
             *
             * That will happen to every page that adopts DataGrid, and the
             * failure is not obvious from either side: the grid is behaving
             * correctly and this warning is unchanged.
             */
            data-testid="schedules-scope-warning"
            className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12.3px] text-amber-900"
          >
            We couldn&apos;t check your group membership just now, so this is showing
            <b> your own schedules only</b>. Try again in a moment.
          </div>
        )}

        {!reportJobsOn ? (
          <div className="rounded-xl border border-gray-100 bg-white px-5 py-16 text-center text-sm text-gray-500">
            Scheduling isn&apos;t enabled in this environment.
          </div>
        ) : schedulesState === 'failed' ? (
          /* CC-53052: this branch already existed and was correctly gated ahead of the
             empty state — but with no way out. `Reports.jsx` pairs the same alert with a
             Try again, and without one the user's only recovery is a full page reload,
             which is exactly the thing the page-scoped error boundary exists to avoid.
             `refetch` comes from react-query, so retrying is free. */
          <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p>Couldn&apos;t load your schedules. {error?.message}</p>
            <p className="mt-1 text-[11.5px]">
              This is not an empty list — anything already scheduled is still set up and will still fire.
            </p>
            <button type="button" onClick={refetch} className="btn-cb-toolbar mt-2.5">Try again</button>
          </div>
        ) : schedulesState === 'blocked' ? (
          /* CC-53132: the state that used to render as "You don't have any schedules
             yet." react-query PAUSED the fetch, so there is no request, no error and
             no spinner — nothing to find in a log, on either side. A spinner forever
             would be nearly as bad, so say what actually happened.

             The second sentence is deliberately NOT the catalog page's. There, the
             stake is a list you are looking at; here it is unattended runs, so the
             user's first question is whether their month-end delivery has been
             cancelled. Answer that before anything else. */
          <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-medium">Can&apos;t reach the server, so we haven&apos;t loaded your schedules.</p>
            <p className="mt-1 text-[11.5px]">
              This is not an empty list — anything already scheduled is still set up and will still fire.
              Your connection looks offline; check it and try again.
            </p>
            <button type="button" onClick={() => refetch()} className="btn-cb-toolbar mt-2.5">Try again</button>
          </div>
        ) : !schedulesReady ? (
          /* Everything left is 'loading' or 'idle'. `idle` means pending-but-never-
             started (`enabled: false`), which today can only come from the same flag
             the `!reportJobsOn` branch above already caught — so it cannot strand
             this page on a spinner. It rides here rather than being left unhandled
             because this is a ternary CHAIN: an uncovered state would fall through
             to `schedules.length === 0` and print the empty state again, which is
             the exact bug being fixed. */
          <div className="rounded-xl border border-gray-100 bg-white px-5 py-16 text-center text-sm text-gray-500">
            Loading your schedules…
          </div>
        ) : schedules.length === 0 ? (
          /* Empty means empty — never a sample row.
             2026-08-27: this card used to carry a SECOND `btn-cb-primary` — labelled
             "Browse reports", pointing at the same URL as the header's "New schedule"
             — and copy describing the from-a-report flow as the only way in. Two
             primaries on one screen, aimed at one destination, under two names. Now
             the header owns the primary action, this card offers the same action at
             toolbar weight under a distinct name (so neither steals the other's
             accessible name), and the from-a-report route is a sentence with an
             inline link, which is what it always was. */
          <div className="rounded-xl border border-cb-accent/30 bg-cb-light p-5">
            <div className="flex gap-3">
              <InfoIcon className="mt-0.5 w-4 h-4 shrink-0 text-cb-accent-text" />
              <div className="text-[12.8px] leading-relaxed text-cb-navy">
                {/*
                  * SCOPE-AWARE (QA, 2026-09-03). This said "You don't have any
                  * schedules yet." at every scope — so a user who switched to
                  * "Shared with the firm" and saw it was told something about
                  * THEMSELVES when the answer was about their colleagues. Same
                  * empty-vs-absent discipline this page already applies to a failed
                  * read one branch up: name whose absence it is.
                  */}
                <p className="mb-2">
                  <b>
                    {scope === 'mine'
                      ? "You don't have any schedules yet."
                      : scope === 'group'
                        ? 'Nobody in your group has shared a schedule, and you have none of your own.'
                        : 'Nobody at the firm has shared a schedule, and you have none of your own.'}
                  </b>
                </p>
                <p>
                  Create one here — you pick the report and set its filters in the dialog. Or{' '}
                  <Link to={REPORTS_LIST_PATH} className="font-semibold text-cb-accent-text underline">
                    open a report
                  </Link>{' '}
                  first, set the filters you want, then choose <b>Set up a recurring schedule…</b>{' '}
                  from the Run button. Either way, pick a rolling date range and the report keeps
                  meaning the same thing every time it runs.
                </p>
              </div>
            </div>
            <div className="mt-4">
              <button type="button" onClick={picker.open} className="btn-cb-toolbar">
                Create your first schedule
              </button>
            </div>
          </div>
        ) : (
          <>
            {/*
              * Himaad's Section 5 wraps the rows in a CARD with a header, so the
              * table is a bounded object on the page rather than rows floating on
              * the background.
              *
              * The header carries two sentences and both are load-bearing:
              *
              *   1. the count, which the table itself no longer states now that
              *      the rows are uniform;
              *   2. "Paused schedules stay in the list and do not run" — the
              *      design's own copy, and the answer to the question a paused row
              *      raises. Without it a reader has to infer whether a greyed
              *      cadence means "off" or "broken".
              *
              * ⚠️ The 100-cap disclosure is NOT decoration. `GET /reports/schedules`
              * calls `listSchedules(..., 100, ...)` with no cursor, so schedule 101
              * does not exist as far as this page is concerned — and a table invites
              * the assumption that it shows everything. A silently truncated list of
              * standing financial instructions is not something to ship quietly.
              * Cursor paging on that route would let this line go.
              */}
            <div className="overflow-hidden rounded-rc-card border border-rc-line bg-white shadow-rc-card">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-rc-line px-4 py-3">
                <span className="text-[12.5px] font-semibold text-cb-navy">
                  {schedules.length} {schedules.length === 1 ? 'schedule' : 'schedules'}
                </span>
                <span className="text-[11.5px] text-gray-500">
                  Paused schedules stay in the list and do not run.
                </span>
                {schedules.length >= SCHEDULES_PAGE_CAP && (
                  <span className="text-[11.5px] font-medium text-amber-700">
                    Showing your first {SCHEDULES_PAGE_CAP} — there may be more.
                  </span>
                )}
              </div>
              <SchedulesTable
                schedules={schedules}
                reportNames={reportNames}
                loading={false}
                runningId={runningId}
                onTogglePaused={togglePaused}
                onEdit={setEditing}
                onRunNow={runNow}
                onSkip={skipNext}
                onDuplicate={duplicate}
                onViewHistory={setHistory}
                onDelete={setConfirmDelete}
              />
            </div>

            {/*
              * Retention, stated once for the page rather than per row — and stating
              * BOTH rules that bound a scheduled result.
              *
              * A schedule keeps its last `retain_count` runs (default 5), not one.
              * The old copy was the mock's "Only the most recent is kept", which
              * understates it: a user reading that would not go looking for the
              * four earlier results they still have.
              *
              * Spec 152 review: this also said the schedule keeps them "rather than
              * expiring them on a clock". False. The row carries `expires_at = null`,
              * but the results bucket deletes every stored result by AGE (7 days on
              * QA, read from its lifecycle rule) whatever the row says, and the count
              * is now enforced the moment each run FINISHES, not when the schedule next
              * fires. The age comes from the one retention module and the worker's
              * declaration; with none declared, only the count rule is stated.
              */}
            <div className="mt-3.5 flex gap-3 rounded-xl border border-cb-accent/30 bg-cb-light px-4 py-3">
              <InfoIcon className="mt-0.5 w-4 h-4 shrink-0 text-cb-accent-text" />
              <p className="text-[12px] leading-relaxed text-cb-navy" data-testid="schedules-retention-note">
                <b>Results, not just runs.</b> A schedule produces a new result each time it runs,
                and keeps its last few runs. Set how many under <i>Keep results</i> when you edit it.
                {schedulesPageRetention(resultMaxAgeDays) ? ` ${schedulesPageRetention(resultMaxAgeDays)}` : ''}
              </p>
            </div>

            {/* Kept, collapsed, and still labelled an experiment. The design has
                no slot for it, which is not the same as asking for it to go. */}
            <ScheduleCalendarPanel schedules={schedules} onSendFeedback={sendFeedback} />
          </>
        )}
      </div>

      {/* Creation. Both hosts spread the SAME `modalProps` bundle, so neither can
          quietly lose `columnsError` or `unsupported`, the two guards that refuse to
          book a run whose filters we could not read.

          But the bundle is NOT the whole contract, and this comment used to claim it
          was — it said the two render sites were "identical by construction", which
          stopped being true the moment CC-53132 added a branch OUTSIDE the spread.
          For three weeks this page opened an empty picker on an unreadable catalog
          while the Queue page said so, and the sentence asserting they could not
          diverge is exactly why nobody looked. A safety property asserted in a
          comment is a test that has not been written yet; the test now exists
          (`ReportSchedules.paused.test.jsx`).

          CC-53132: when the CATALOG read itself did not resolve there is nothing to
          pick from, and an empty picker is the lie. Say so instead. */}
      {picker.isOpen && (picker.catalogUnreadable ? (
        <QueueCatalogUnreadable
          state={picker.catalogState}
          error={picker.catalogError}
          onRetry={picker.retryCatalog}
          onClose={picker.close}
        />
      ) : (
        <QueueReportModal {...picker.modalProps} />
      ))}

      {/* Spec 169: a colleague's private schedule (only ever offered to an administrator,
          `adminAccess`) opens the administrator's editor, which cannot send a delivery,
          sharing or filter field. A colleague's row WITHOUT `adminAccess` opens nothing. */}
      {editing && editing.isOwner !== false && (
        <EditScheduleModal
          schedule={editing}
          saving={save.isPending}
          onSave={(payload) => save.mutate({ id: editing.id, payload })}
          onClose={() => setEditing(null)}
        />
      )}
      {editing && editing.isOwner === false && editing.adminAccess === true && (
        <AdminEditScheduleModal
          schedule={editing}
          saving={save.isPending}
          onSave={(payload) => save.mutate({ id: editing.id, payload })}
          onClose={() => setEditing(null)}
        />
      )}

      {history && <ScheduleRunHistory schedule={history} onClose={() => setHistory(null)} />}

      {confirmDelete && (
        <Modal
          // Spec 216: never "Report #exago/<guid>"; an Advanced Report schedule's name is masked.
          title={<FirmText reportId={confirmDelete.reportId}>{`Delete "${confirmDelete.name || unnamedReportLabel(confirmDelete.reportId)}"?`}</FirmText>}
          onClose={() => setConfirmDelete(null)}
          maxWidth="max-w-md"
          footer={(
            <>
              {/* Pause is offered FIRST and as the quiet-but-primary path: most
                  people reaching for Delete actually want to stop it for a
                  month-end close, and delete throws the configuration away. */}
              <button
                type="button"
                onClick={() => { togglePaused({ ...confirmDelete, enabled: true }); setConfirmDelete(null) }}
                className="btn-cb-primary"
              >
                Pause instead
              </button>
              <span className="flex-1" />
              <button type="button" onClick={() => setConfirmDelete(null)} className="btn-cb-toolbar">Cancel</button>
              <button
                type="button"
                onClick={() => remove.mutate(confirmDelete.id)}
                disabled={remove.isPending}
                className="btn-cb-toolbar font-semibold text-red-600"
              >
                {remove.isPending ? 'Deleting…' : 'Delete schedule'}
              </button>
            </>
          )}
        >
          <div className="px-5 py-4 text-[12.8px] leading-relaxed text-gray-700">
            {/* Name the cadence and the audience: the design's copy is specific
                because "are you sure?" does not tell you what you are destroying. */}
            <p>
              {/* Spec 169: a private schedule is visible to Centerbase administrators too,
                  and a group one to its group, so "only you" was true of neither. */}
              This schedule runs <b>{confirmDelete.description}</b> and is visible to{' '}
              <b>{deleteAudience(confirmDelete)}</b>. Deleting it
              stops all future runs and removes the filter set you tuned. Results already in your
              queue are unaffected.
            </p>
            <p className="mt-2 text-gray-500">
              If you only need a break, <b>pause</b> it instead — a paused schedule keeps its
              filters and can be resumed anytime.
            </p>
          </div>
        </Modal>
      )}
    </>
  )
}

/**
 * Edit an existing schedule: name, recurrence, roll-forward, retention, visibility
 * — and, as of spec 111, DELIVERY.
 *
 * ## Why delivery had to be here (S4-D11)
 *
 * It was settable only at CREATION. So a schedule created without email could
 * never be given it, and one created with email could never have it turned off,
 * without deleting the schedule and rebuilding it — losing the tuned filter set,
 * which is the same thing the delete confirmation exists to protect.
 *
 * The route already accepted it: `PUT /reports/schedules/:id` reads
 * `body.deliverEmail` and `body.deliverFormat` (`schedules.js:370`) and has since
 * delivery shipped. Only the UI was missing, so this is a form field, not a
 * feature.
 *
 * **The recipient is not an input and must never become one.** CC-53046 removed
 * `deliverTo` from the API because this write path had no owner fallback at all —
 * a PUT wrote whatever it was handed, on an object that then fires on a
 * recurrence. The server derives the recipient from the caller's verified token.
 * Do not add a "send to" field here; a real send-to-a-colleague feature needs the
 * recipient AUTHORISED against CB's directory, not merely accepted.
 * ⚠ Superseded by spec 138 (Jordan, 2026-10-07): "Also send to" (`EmailRecipientsField`)
 * adds up to 25 more addresses (10 until spec 201), each validated, not directory-authorised; `deliverTo` is
 * still not an input, and the owner's own copy is still bound to the verified token.
 * ⚠ And by spec 139 (2026-10-08): an owner whose token has NO address supplies one
 * ("Your email address", `deliverOwnerAddress`); every other account is unchanged.
 */
const EditScheduleModal = ({ schedule, onSave, onClose, saving }) => {
  const [name, setName] = useState(schedule.name || '')
  const [recurrence, setRecurrence] = useState(schedule.recurrence || undefined)
  const [rollForward, setRollForward] = useState(schedule.rollForward || 'pinned')
  // Spec 195 — only a generic schedule's runs roll forward (`rollsForward`).
  const rollForwardSupported = rollsForward(schedule)
  const [retainCount, setRetainCount] = useState(schedule.retainCount ?? 5)
  // Spec 152 — the results bucket's age, for the "Keep results" sentence.
  const resultMaxAgeDays = useResultMaxAgeDays()
  // Seeded from the row so the form opens showing what is actually set — the
  // schedules list already carries both fields (`schedules.js:110-112`).
  const [deliverEmail, setDeliverEmail] = useState(!!schedule.deliverEmail)
  // Spec 194 — the formats THIS schedule's own copy can be attached in, as cb-api decides
  // them (`attachFormats` on the row, from the table its mailer reads): an accounting
  // statement or a listed specialized report has its page's export formats. A payload
  // without the field (an older one) falls back to the kind: both for a generic schedule,
  // none otherwise.
  const kindIsGeneric = schedule.kind == null || schedule.kind === 'generic'
  const ownFormats = Array.isArray(schedule.attachFormats) ? schedule.attachFormats : (kindIsGeneric ? ['xlsx', 'csv'] : [])
  // Spec 200 — other recipients, and an address the owner supplies, are offered wherever
  // the schedule's runs can be ATTACHED: cb-api judges them by the same `attachFormats`
  // (it used to refuse both on any non-generic kind, spec 137 *review*).
  const attachSupported = ownFormats.length > 0
  // A schedule saved with a format its runs cannot be attached in opens on what it
  // actually does — a link — rather than showing an attach option it cannot keep.
  const [deliverFormat, setDeliverFormat] = useState(
    ownFormats.includes(schedule.deliverFormat) ? schedule.deliverFormat : 'link'
  )
  /*
   * Spec 138 — the schedule's other recipients, seeded from the row (owner-only in the
   * API, so this is the owner's own list). The owner line reads the row's `deliverTo`:
   * the address the SERVER resolved for this schedule, not a client guess.
   */
  const [recipients, setRecipients] = useState(() => (attachSupported && Array.isArray(schedule.deliverRecipients) ? schedule.deliverRecipients : []))
  const [formatSwitchNote, setFormatSwitchNote] = useState(null)
  const hasRecipients = attachSupported && deliverEmail && recipients.length > 0
  /*
   * Spec 139 §2.7 — the owner's own address, and whether to ask for one.
   *
   * *review* — decided by the LIVE access token, which is what the server judges this
   * save by (`hasUsableOwnEmail`) and what every send uses. The row's `deliverTo` is only
   * what the server resolved at the LAST write: an account that has lost its email claim
   * since (a changed sign-in connection, say) still has it set, and reading the row first
   * hid the input from exactly that owner while the server refused every save with a
   * sentence pointing at it. So: the token when it can be read (its address, or none —
   * then ask, seeded from `deliverOwnerAddress`); the row only while the token is being
   * read, or when it cannot be (then no `deliverTo` means no address, so ask). An account
   * that HAS an address since is shown it, and the save rebinds `deliver_to` to it.
   */
  const liveOwner = useOwnDeliveryAddress(true)
  let owner
  if (liveOwner.status === 'ready') owner = liveOwner
  else if (schedule.deliverTo) owner = { status: 'ready', address: schedule.deliverTo }
  else if (liveOwner.status === 'loading') owner = liveOwner
  else owner = { status: 'ready', address: null }
  const [ownerAddress, setOwnerAddress] = useState(() => schedule.deliverOwnerAddress || '')
  // A count of refused saves, not a flag: each refusal is news to the field (*review*).
  const [ownerAddressAttempted, setOwnerAddressAttempted] = useState(0)
  const ownerAddressRef = useRef(null)
  const needsOwnerAddress = attachSupported && deliverEmail && ownerNeedsAddress(owner)
  const onRecipientsChange = (next) => {
    if (next.length > 0 && recipients.length === 0 && deliverFormat === 'link') {
      // Spec 200: to a format this schedule's report really has (GL Budget: CSV only).
      const to = recipientsFormatSwitch(ownFormats)
      setDeliverFormat(to.format)
      setFormatSwitchNote(to.note)
    }
    if (next.length === 0) setFormatSwitchNote(null)
    setRecipients(next)
  }
  /*
   * *review* — the one toggle governs the OTHER recipients too (cb-api uses the list only
   * while `deliver_email=1`), so with a list it says so in its own name; and switching it
   * off says the list is kept, because the field that shows it is hidden then. An owner
   * stopping only their own copy used to stop everyone's, silently.
   *
   * Spec 139: always "you and N" now. An owner with no address of their own can no
   * longer email the others without supplying one, and their copy goes to it — so the
   * spec 138 wording for that owner ("to N other addresses") would be untrue.
   */
  const others = attachSupported ? recipients.length : 0
  const emailToggleLabel = others > 0
    ? `Email each result to you and ${others} other address${others === 1 ? '' : 'es'}`
    : 'Email me each result'
  const [scope, setScope] = useState(schedule.scope || (schedule.shareable ? 'firm' : 'mine'))
  const [groupId, setGroupId] = useState('')
  const [groups, setGroups] = useState(null) // null = not loaded yet
  const [groupsFailed, setGroupsFailed] = useState(false)

  // Groups load lazily, only if the user actually picks the group scope. Reuses the
  // SAME endpoint saved-view sharing uses: it returns only groups you BELONG to,
  // because sharing is restricted to those. /security/groups would offer options
  // that can only ever 403 (CC-49595 — 7 of 10 vladdemo groups have no members).
  useEffect(() => {
    if (scope !== 'group' || groups !== null || groupsFailed) return
    let alive = true
    reportViewsService.groups()
      .then((g) => { if (alive) setGroups(g || []) })
      // CC-53132, the same defect one layer out: this used to `setGroups([])`, which
      // rendered as "No groups found" — byte-identical to the answer an account that
      // genuinely belongs to no group gets. One is a fact about you and is worth
      // acting on; the other is a fact about the network and is worth retrying, and
      // conflating them is what hid CC-49595 through two QA rounds.
      .catch(() => { if (alive) setGroupsFailed(true) })
    return () => { alive = false }
  }, [scope, groups, groupsFailed])

  const scopeIncomplete = scope === 'group' && !groupId

  // The name is the row's identity line, so it cannot be blank (CC-50240 item 8).
  // Enforced here rather than only server-side so the person fixing it is the one
  // who knows what the schedule is for.
  const nameError = name.trim().length === 0

  const save = () => {
    // Spec 139 §2.7 — blocked inline with the server's own sentence; the server refuses too.
    if (needsOwnerAddress && ownerAddressError(ownerAddress)) {
      setOwnerAddressAttempted((n) => n + 1)
      ownerAddressRef.current?.focus()
      return
    }
    onSave({
      name: name.trim(), recurrence, retainCount, deliverEmail,
      // Spec 195: never a token for a schedule whose runs cannot apply one.
      ...(rollForwardSupported ? { rollForward } : {}),
      // Spec 138: with other recipients the format travels even when email is
      // being switched off, so the server validates the list it keeps against
      // the format it will keep (never a stored Link).
      deliverFormat: deliverEmail || (attachSupported && recipients.length > 0) ? deliverFormat : undefined,
      // The list REPLACES the stored one ([] clears it). A schedule that cannot
      // attach sends nothing about recipients, so its stored value is untouched.
      ...(attachSupported ? { deliverRecipients: recipients } : {}),
      // Spec 139: the owner's own address, only while it is being asked for. Absent
      // otherwise, which leaves the stored one as it is.
      ...(needsOwnerAddress ? { deliverOwnerAddress: normalizeOwnerAddress(ownerAddress) } : {}),
      scope, groupId: scope === 'group' ? groupId : null,
      groupName: scope === 'group' ? (groups || []).find((g) => String(g.id) === String(groupId))?.name || null : null,
    })
  }

  return (
    <Modal
      title="Edit schedule"
      subtitle={reportNumberOf(schedule) ? `Report #${reportNumberOf(schedule)}` : undefined}
      onClose={onClose}
      maxWidth="max-w-xl"
      footer={(
        <>
          <span className="flex-1" />
          <button type="button" onClick={onClose} className="btn-cb-toolbar">Cancel</button>
          <button
            type="button"
            disabled={saving || nameError || scopeIncomplete}
            onClick={save}
            className="btn-cb-primary"
          >
            {saving ? 'Saving…' : 'Save schedule'}
          </button>
        </>
      )}
    >
      <div className="grid gap-3.5 px-5 py-5">
        <div>
          <label htmlFor="sched-name" className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
            Schedule name
          </label>
          <input
            id="sched-name"
            type="text"
            value={name}
            maxLength={80}
            required
            aria-invalid={nameError}
            aria-describedby={nameError ? 'sched-name-error' : undefined}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Monday morning billable hours"
            /* BOTH states move: red-300 (#fca5a5, ~2.0:1) failed the same 3.0:1
               floor as gray-200, and on the error state the border is carrying
               MORE meaning, not less. red-500 is 3.76:1 on white.
               Spec 216: an Advanced Report schedule's name is the firm's by default. */
            className={`w-full rounded-lg border px-2.5 py-1.5 text-[12.5px] ${nameError ? 'border-red-500' : 'border-rc-control'} ${firmTextClass(schedule.reportId)}`.trim()}
          />
          {nameError && (
            <p id="sched-name-error" className="mt-1.5 text-[11.5px] text-red-600">
              Give this schedule a name — it&apos;s how you&apos;ll recognise it in this list.
            </p>
          )}
        </div>

        <RecurrenceEditor
          value={recurrence}
          onChange={setRecurrence}
          rollForward={rollForward}
          onRollForwardChange={setRollForward}
          rollForwardSupported={rollForwardSupported}
          idPrefix="sched"
        />

        {/* Visibility (CC-50282). Sharing is view-only, and the copy says so — a
            colleague seeing a schedule is very different from a colleague being
            able to stop it, and the difference matters for a month-end pack. */}
        <div>
          <label htmlFor="sched-scope-edit" className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
            Who can see this
          </label>
          <select
            id="sched-scope-edit"
            value={scope}
            onChange={(e) => { setScope(e.target.value); setGroupId('') }}
            className="w-full rounded-lg border border-rc-control px-2.5 py-1.5 text-[12.5px]"
          >
            {/* Spec 169: Centerbase administrators can see (and edit) a private schedule. */}
            <option value="mine">{PRIVATE_SCOPE_OPTION_LABEL}</option>
            <option value="group">A group…</option>
            <option value="firm">Everyone at the firm</option>
          </select>
          {scope === 'group' && (
            <>
              {/* `border-rc-control` (#8b95a5, 3.03:1) not `border-gray-200`
                  (1.24:1) — SC 1.4.11 needs 3.0:1 on a control boundary. Kept
                  deliberately while merging this branch's honesty change in. */}
              <select
                aria-label="Group to share with"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className="mt-2 w-full rounded-lg border border-rc-control px-2.5 py-1.5 text-[12.5px]"
              >
                <option value="">
                  {groupsFailed
                    ? `Couldn't load your groups`
                    : groups === null ? 'Loading groups…' : groups.length === 0 ? 'No groups found' : 'Pick a group…'}
                </option>
                {(groups || []).map((g) => <option key={g.id} value={String(g.id)}>{g.name}</option>)}
              </select>
              {groupsFailed && (
                <p role="alert" className="mt-1.5 text-[11.5px] text-amber-900">
                  We couldn&apos;t reach the server, so this list is empty for a reason that isn&apos;t you —
                  it is not that you belong to no groups.{' '}
                  <button
                    type="button"
                    onClick={() => setGroupsFailed(false)}
                    className="font-semibold text-cb-accent-text underline"
                  >
                    Try again
                  </button>
                </p>
              )}
            </>
          )}
          {/* Spec 169 (Jordan, 2026-10-13): a private schedule is the owner's AND the
              administrators'. They can see it, edit or pause it, and open its results; it
              always runs as its owner. A shared one is unchanged: view-only for others. */}
          <p className="mt-1.5 text-[11.5px] text-gray-500" data-testid="sched-scope-help">
            {scope === 'mine'
              ? PRIVATE_SCHEDULE_HELP
              : <>Sharing lets colleagues <b>see</b> this schedule and whether it&apos;s running. Only you can pause, edit, run or delete it — and only you can open its results.</>}
          </p>
        </div>

        <div>
          <label htmlFor="sched-retain" className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
            Keep results
          </label>
          <select
            id="sched-retain"
            value={retainCount}
            onChange={(e) => setRetainCount(Number(e.target.value))}
            className="w-full rounded-lg border border-rc-control px-2.5 py-1.5 text-[12.5px]"
          >
            {[3, 5, 12, 24].map((n) => <option key={n} value={n}>Last {n} runs</option>)}
          </select>
          {/* Spec 152 (decision 4): "kept by count, not by age" was false — the results
              bucket deletes every result by age (about 7 days on QA, 2 on prod). Both
              bounds, from the one retention module; no age when none is declared. */}
          <p className="mt-1.5 text-[11.5px] text-gray-500" data-testid="sched-retain-help">
            {scheduleRetentionHelp({ retainCount, maxAgeDays: resultMaxAgeDays })}
          </p>
        </div>

        {/* Delivery — see the note on this component for why it belongs here. */}
        <div>
          <ToggleSwitch
            checked={deliverEmail}
            onChange={() => setDeliverEmail((v) => !v)}
            aria-label={emailToggleLabel}
            label={emailToggleLabel}
            labelClassName="mb-1.5 text-[12.3px] text-gray-700"
          />
          {!deliverEmail && others > 0 && (
            <p className="ml-[52px] text-[11.5px] text-gray-600" data-testid="recipients-kept-note">
              {keptRecipientsNote(others)}
            </p>
          )}
          {deliverEmail && (
            <div className="ml-[52px]">
              <label htmlFor="sched-deliver-format" className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
                Send as
              </label>
              <select
                id="sched-deliver-format"
                value={deliverFormat}
                aria-describedby="sched-deliver-format-hint"
                onChange={(e) => {
                  setDeliverFormat(e.target.value)
                  // The person chose: an earlier switch note no longer describes the format.
                  setFormatSwitchNote(null)
                }}
                className="w-full rounded-lg border border-rc-control px-2.5 py-1.5 text-[12.5px]"
              >
                {/* PDF is deliberately absent, on both hosts: CB has no server-side
                    report PDF, so offering it would queue a send that cannot be
                    fulfilled. Same three options as the create modal — one
                    vocabulary for one capability. */}
                {/* Spec 138: disabled — not removed — while other addresses are added. */}
                <option value="link" disabled={hasRecipients}>A link to open it here</option>
                {ownFormats.includes('xlsx') && <option value="xlsx">Excel file attached</option>}
                {ownFormats.includes('csv') && <option value="csv">CSV file attached</option>}
              </select>
              {/* Spec 137 §2.11: never "from you" — Centerbase chooses the sender. */}
              <p id="sched-deliver-format-hint" className="mt-1.5 text-[11.5px] text-gray-500">
                Goes to {needsOwnerAddress ? 'the address you enter below' : 'the address you sign in with'}, sent through Centerbase’s mail. {deliverFormat === 'link'
                  ? `You sign in to view it.${ownFormats.length ? '' : " This report type can't be attached yet."}`
                  : 'Large files are sent as a link instead.'}
                {hasRecipients && ' Link can’t be chosen while other addresses are added, because a link only opens for you — and a file too large to attach isn’t sent to them.'}
              </p>
              {/* *review* — always mounted, only its content changes (see the create modal). */}
              <div aria-live="polite" data-testid="format-switch-live">
                {hasRecipients && formatSwitchNote && (
                  <p data-testid="format-switch-note" className="mt-1 text-[11.5px] font-semibold text-amber-900">
                    {formatSwitchNote}
                  </p>
                )}
              </div>
              {/* Spec 138 §3.7 — the same field as the create modal; a sentence instead
                  on a schedule whose report cannot be attached. */}
              <EmailRecipientsField
                idPrefix="sched-recipients"
                owner={owner}
                recipients={recipients}
                onChange={onRecipientsChange}
                supported={attachSupported}
                ownerAddress={ownerAddress}
                onOwnerAddressChange={setOwnerAddress}
                ownerAddressAttempted={ownerAddressAttempted}
                ownerAddressInputRef={ownerAddressRef}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

/**
 * Spec 169 (CC-54214) — a CB administrator edits a colleague's PRIVATE schedule.
 *
 * Jordan, 2026-10-13: admins may "see and be able to edit schedules set to mine", and an
 * admin's edit keeps the schedule running as its OWNER (their data access and email
 * address). So this editor offers exactly what cb-api's administrator path accepts —
 * `ADMIN_EDITABLE_BODY_KEYS` in `jobs/schedules.js`: the name, the timing, the date-window
 * preset and "Keep results" (pause / resume is the row's own control). The payload names
 * nothing else; cb-api answers 403 to any other key, so a delivery, sharing or filter
 * field could not be changed from here even by a crafted request.
 *
 * What it deliberately does NOT show: the owner's delivery address, the schedule's other
 * recipients, an address the owner typed, or whether the owner's sign-in has lapsed —
 * cb-api never sends those for a schedule the reader does not own (`toApi`), and an
 * editor that cannot change them has no reason to display them. It says in words that
 * delivery and visibility stay with the owner.
 */
const AdminEditScheduleModal = ({ schedule, onSave, onClose, saving }) => {
  const [name, setName] = useState(schedule.name || '')
  const [recurrence, setRecurrence] = useState(schedule.recurrence || undefined)
  const [rollForward, setRollForward] = useState(schedule.rollForward || 'pinned')
  // Spec 195 — as the owner's editor: only a generic schedule's runs roll forward.
  const rollForwardSupported = rollsForward(schedule)
  const [retainCount, setRetainCount] = useState(schedule.retainCount ?? 5)
  const resultMaxAgeDays = useResultMaxAgeDays()
  const nameError = name.trim().length === 0
  const owner = schedule.ownerName || 'a colleague'
  // Spec 169 *review* — "Keep results" can be raised here, never lowered: cb-api refuses a
  // lower count (403), because the next run would permanently delete the owner's kept
  // results. So a lower choice is shown disabled rather than offered and then refused.
  // What the schedule keeps NOW is read as cb-api reads it (missing or 0 → 5), and it is
  // always one of the choices, so the select can show the stored value even when it is
  // not one of the editor's four.
  const keptNow = Math.max(1, Number(schedule.retainCount) || 5)
  const retainChoices = [...new Set([3, 5, 12, 24, keptNow])].sort((a, b) => a - b)

  return (
    <Modal
      title="Edit schedule"
      subtitle={reportNumberOf(schedule) ? `Report #${reportNumberOf(schedule)} · private to ${owner}` : `Private to ${owner}`}
      onClose={onClose}
      maxWidth="max-w-xl"
      footer={(
        <>
          <span className="flex-1" />
          <button type="button" onClick={onClose} className="btn-cb-toolbar">Cancel</button>
          <button
            type="button"
            disabled={saving || nameError}
            onClick={() => onSave({ name: name.trim(), recurrence, ...(rollForwardSupported ? { rollForward } : {}), retainCount })}
            className="btn-cb-primary"
          >
            {saving ? 'Saving…' : 'Save schedule'}
          </button>
        </>
      )}
    >
      <div className="grid gap-3.5 px-5 py-5">
        <div data-testid="admin-edit-note" className="flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-amber-900">
          <InfoIcon className="mt-0.5 w-4 h-4 shrink-0" />
          <span>
            You’re editing {owner}’s private schedule as a Centerbase administrator. It keeps running as{' '}
            {schedule.ownerName || 'its owner'}, with their access to data, and its email still goes where they set it.
          </span>
        </div>

        <div>
          <label htmlFor="admin-sched-name" className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
            Schedule name
          </label>
          <input
            id="admin-sched-name"
            type="text"
            value={name}
            maxLength={80}
            required
            aria-invalid={nameError}
            aria-describedby={nameError ? 'admin-sched-name-error' : undefined}
            onChange={(e) => setName(e.target.value)}
            className={`w-full rounded-lg border px-2.5 py-1.5 text-[12.5px] ${nameError ? 'border-red-500' : 'border-rc-control'} ${firmTextClass(schedule.reportId)}`.trim()}
          />
          {nameError && (
            <p id="admin-sched-name-error" className="mt-1.5 text-[11.5px] text-red-600">
              Give this schedule a name — it&apos;s how people recognise it in this list.
            </p>
          )}
        </div>

        <RecurrenceEditor
          value={recurrence}
          onChange={setRecurrence}
          rollForward={rollForward}
          onRollForwardChange={setRollForward}
          rollForwardSupported={rollForwardSupported}
          idPrefix="admin-sched"
        />

        <div>
          <label htmlFor="admin-sched-retain" className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
            Keep results
          </label>
          <select
            id="admin-sched-retain"
            value={retainCount}
            onChange={(e) => setRetainCount(Number(e.target.value))}
            aria-describedby="admin-sched-retain-raise-only"
            className="w-full rounded-lg border border-rc-control px-2.5 py-1.5 text-[12.5px]"
          >
            {retainChoices.map((n) => <option key={n} value={n} disabled={n < keptNow}>Last {n} runs</option>)}
          </select>
          <p className="mt-1.5 text-[11.5px] text-gray-500">
            {scheduleRetentionHelp({ retainCount, maxAgeDays: resultMaxAgeDays })}
          </p>
          <p id="admin-sched-retain-raise-only" data-testid="admin-retain-raise-only" className="mt-1 text-[11.5px] text-gray-600">
            You can raise this, but not lower it: lowering it would permanently delete results {owner} has kept. Only they can lower it.
          </p>
        </div>

        <div data-testid="admin-edit-owner-only">
          <div className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
            Who can see this and email delivery
          </div>
          <p className="text-[12.5px] leading-relaxed text-gray-700">
            Only {schedule.ownerName || 'its owner'} and Centerbase administrators can see this schedule.
            Who can see it, its email delivery and its filters stay as its owner set them; only they can change those.
          </p>
        </div>
      </div>
    </Modal>
  )
}

export default ReportSchedules
